import React, {ReactNode} from 'react';
import {To, useNavigate} from 'react-router';
import clsx from 'clsx';
import {diApi} from '@di-shared/api/di';
import {emitEventLog} from '@di-shared/features/analytics';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useOdometer from '@di-shared/features/mileage/useOdometer';
import LinkTo, {LinkToProps} from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';

interface VehicleSestItemKrProps {
  vin: string;
  title: string;
  badges: BadgeType[];
  to: To;
  icon: ReactNode;
}

interface BadgeType {
  title: string;
  isWarning: boolean;
}

function VehicleSestItemKr({title, badges, vin, to, icon}: VehicleSestItemKrProps) {
  const {t} = useInferableTranslation();
  const badgeCount = badges.length;
  const warningCount = badges.filter((badge) => badge.isWarning).length;
  if (!badgeCount) {
    return null;
  }
  return (
    <li className={clsx('consumable_item', {type_warning: warningCount > 0})}>
      <ConditionalLinkTo
        vin={vin}
        className="consumable_link"
        to={to}
        onClickBypass={() =>
          emitEventLog({
            event: 'vehicle_status_check',
            interaction_type: 'view engine consumables',
            click_text: title,
            click_location: 'body',
          })
        }
      >
        <div className="consumable_icon_box">{icon}</div>
        <div className="consumable_info">
          <strong className="consumable_info_title">
            {title}
            {warningCount > 0 && (
              <span className="count">
                <span className="blind">{t('T10117')}</span>
                {warningCount}
              </span>
            )}
          </strong>

          <div className="badge_box">
            {badges.map(({title, isWarning}) => (
              <span
                key={title}
                className={clsx('badge badge_outline', {type_warning: isWarning})}
              >
                {title}
                {isWarning && <span className="blind">{t('T10118')}</span>}
              </span>
            ))}
          </div>
        </div>
        <span
          className="ic ic_arrow_forward_16px"
          aria-hidden="true"
        >
          <span className="blind">{t('T10063')}</span>
        </span>
      </ConditionalLinkTo>
    </li>
  );
}

function ConditionalLinkTo({children, vin, ...props}: LinkToProps & {vin: string}) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {resolveError, getErrorCode} = useApiError();
  const {convertDistanceSymbol, convertDistanceValue, revertDistanceValue} = useUnitConverter();
  const {openConfirm} = useCcWebviewUtilsHelper();
  const {odometer, updatedDate, refetch: refetchOdometer} = useOdometer(vin);

  async function updateOdometer(newOdometer: number) {
    try {
      await diApi.maintenancesSestController.updateOdometer({
        vin,
        requestBody: {value: revertDistanceValue(newOdometer)},
      });
    } catch (error) {
      const errorCode = getErrorCode(error);
      if (errorCode === 4001 && newOdometer) {
        const isConfirm = await openConfirm({
          title: t('T30155'),
          message: t('T30605_$odometer', {
            odometer: `${formatNumberWithCommas(convertDistanceValue(newOdometer))}${convertDistanceSymbol(newOdometer)}`,
          }),
          okText: t('T30592'),
          cancelText: t('T30593'),
        });
        if (isConfirm) {
          navigate(ROUTE_PATHS.DIAG_EXCHANGE_HISTORY.getFullPath(vin));
          return;
        }
        await openUpdateOdometerBottomSheet();
        return;
      }
      // 에러처리: 4001 외(일반 오류)
      const [execute] = await resolveError(error);
      execute();
    }
  }

  async function openUpdateOdometerBottomSheet() {
    const newOdometer = await ccWebviewUtils.openBottomSheetWebViewUrl<number>(
      ROUTE_PATHS.BOTTOM_SHEET_DIAG_UPDATE_ODOMETER.getFullPath(),
      {isOdometerDataExist: !!updatedDate, odometer, updatedAt: updatedDate}
    );
    if (!newOdometer) return;

    await updateOdometer(newOdometer);
    await refetchOdometer();
  }

  if (odometer) {
    return <LinkTo {...props}>{children}</LinkTo>;
  }
  return (
    <a
      role="button"
      className="consumable_link"
      onClick={openUpdateOdometerBottomSheet}
    >
      {children}
    </a>
  );
}

export default VehicleSestItemKr;
